import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { Alert, CustomInput, FormGroup } from 'reactstrap';

import Widget from '../../../components/Widget';
import { loadProductCatalogIfNeeded } from '../../../actions/productCatalog';
import {
  loadMenuDetail,
  replaceMenuCategories,
  upsertMenuPrices,
  clearMenuPrice,
  upsertMenuOptionPrices,
  clearMenuOptionPrice,
  fetchMenuSchedules,
  replaceMenuSchedules,
} from '../../../actions/menus';
import s from './Menus.module.scss';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function timeToInput(value) {
  if (value == null) return '09:00';
  if (typeof value === 'string') {
    // "09:00:00" or "09:00"
    return value.length >= 5 ? value.slice(0, 5) : value;
  }
  // ASP.NET TimeSpan as { Hours, Minutes } or total ticks — uncommon via JSON
  return '09:00';
}

function windowKind(start, end) {
  if (!start || !end) return '';
  if (start === end) return 'All day';
  if (end <= start) return 'Overnight';
  return 'Until end';
}

class MenuDetail extends React.Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    match: PropTypes.object.isRequired,
    detail: PropTypes.object,
    categories: PropTypes.array,
    products: PropTypes.array,
    isReceiving: PropTypes.bool,
    isUpdating: PropTypes.bool,
  };

  state = {
    selectedCategoryIds: [],
    priceDrafts: {},
    savingCategories: false,
    savingPrices: false,
    scheduleDrafts: [],
    scheduleLoading: false,
    scheduleError: '',
    savingSchedule: false,
  };

  static priceKey = (productId, variationId = 0) => `${productId}|${Number(variationId) || 0}`;
  static optionKey = (optionId) => `opt|${Number(optionId) || 0}`;

  componentDidMount() {
    this.props.dispatch(loadProductCatalogIfNeeded());
    this.loadDetail();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.menuId !== this.props.match.params.menuId) {
      this.loadDetail();
    }
    if (prevProps.detail !== this.props.detail && this.props.detail) {
      const prices = {};
      (this.props.detail.Prices || []).forEach((p) => {
        prices[MenuDetail.priceKey(p.ProductId, p.VariationId)] = String(p.Price);
      });
      (this.props.detail.OptionPrices || []).forEach((p) => {
        prices[MenuDetail.optionKey(p.OptionId)] = String(p.Price);
      });
      this.setState({
        selectedCategoryIds: [...(this.props.detail.CategoryIds || [])],
        priceDrafts: prices,
      });
    }
  }

  loadDetail = () => {
    const menuId = Number(this.props.match.params.menuId);
    if (menuId > 0) {
      this.props.dispatch(loadMenuDetail(menuId));
      this.loadSchedules(menuId);
    }
  };

  loadSchedules = async (menuId) => {
    this.setState({ scheduleLoading: true, scheduleError: '' });
    try {
      const rows = await fetchMenuSchedules(menuId);
      this.setState({
        scheduleDrafts: (rows || []).map((r) => ({
          DayOfWeek: Number(r.DayOfWeek ?? r.dayOfWeek ?? 0),
          StartTime: timeToInput(r.StartTime ?? r.startTime),
          EndTime: timeToInput(r.EndTime ?? r.endTime),
        })),
        scheduleLoading: false,
      });
    } catch (err) {
      this.setState({
        scheduleLoading: false,
        scheduleError: err?.message || 'Failed to load schedules',
        scheduleDrafts: [],
      });
    }
  };

  addScheduleRow = () => {
    this.setState((prev) => ({
      scheduleDrafts: [
        ...prev.scheduleDrafts,
        { DayOfWeek: 1, StartTime: '09:00', EndTime: '17:00' },
      ],
    }));
  };

  updateScheduleRow = (index, field, value) => {
    this.setState((prev) => {
      const next = prev.scheduleDrafts.map((row, i) => (
        i === index ? { ...row, [field]: value } : row
      ));
      return { scheduleDrafts: next };
    });
  };

  removeScheduleRow = (index) => {
    this.setState((prev) => ({
      scheduleDrafts: prev.scheduleDrafts.filter((_, i) => i !== index),
    }));
  };

  saveSchedules = async () => {
    const menuId = Number(this.props.match.params.menuId);
    const incomplete = (this.state.scheduleDrafts || []).some((row) => !row.StartTime || !row.EndTime);
    if (incomplete) {
      this.setState({ scheduleError: 'Each window needs a start and end time.' });
      return;
    }
    this.setState({ savingSchedule: true, scheduleError: '' });
    const saved = await this.props.dispatch(replaceMenuSchedules(menuId, this.state.scheduleDrafts));
    if (saved) {
      this.setState({
        scheduleDrafts: saved.map((r) => ({
          DayOfWeek: Number(r.DayOfWeek ?? r.dayOfWeek ?? 0),
          StartTime: timeToInput(r.StartTime ?? r.startTime),
          EndTime: timeToInput(r.EndTime ?? r.endTime),
        })),
      });
    }
    this.setState({ savingSchedule: false });
  };

  toggleCategory = (categoryId) => {
    this.setState((prev) => {
      const set = new Set(prev.selectedCategoryIds);
      if (set.has(categoryId)) set.delete(categoryId);
      else set.add(categoryId);
      return { selectedCategoryIds: Array.from(set) };
    });
  };

  saveCategories = async () => {
    const menuId = Number(this.props.match.params.menuId);
    this.setState({ savingCategories: true });
    await this.props.dispatch(replaceMenuCategories(menuId, this.state.selectedCategoryIds));
    this.setState({ savingCategories: false });
  };

  menuProducts = () => {
    const { products } = this.props;
    const selected = new Set(this.state.selectedCategoryIds);
    return (products || []).filter((p) => selected.has(p.CategoryId));
  };

  /** Sellable units + options for the price table. */
  priceRows = () => {
    const rows = [];
    this.menuProducts().forEach((p) => {
      const variations = (p.Variations || []).filter((v) => v && !v.IsDeleted);
      if (variations.length === 0) {
        rows.push({
          kind: 'product',
          key: MenuDetail.priceKey(p.ProductId, 0),
          productId: p.ProductId,
          variationId: 0,
          label: p.Name,
          base: Number(p.Price || 0),
          baseLabel: `$${Number(p.Price || 0).toFixed(2)}`,
        });
      } else {
        variations.forEach((v) => {
          rows.push({
            kind: 'product',
            key: MenuDetail.priceKey(p.ProductId, v.VariationId),
            productId: p.ProductId,
            variationId: Number(v.VariationId) || 0,
            label: `${p.Name} — ${v.Name}`,
            base: Number(v.Cost || 0),
            baseLabel: `$${Number(v.Cost || 0).toFixed(2)}`,
          });
        });
      }

      const options = (p.Options || []).filter((o) => o && !o.IsDeleted);
      options.forEach((o) => {
        const hasCost = o.Cost != null && o.Cost !== '';
        rows.push({
          kind: 'option',
          key: MenuDetail.optionKey(o.OptionId),
          optionId: Number(o.OptionId) || 0,
          productId: p.ProductId,
          label: `${p.Name} — ${o.Name} (option)`,
          base: hasCost ? Number(o.Cost) : null,
          baseLabel: hasCost ? `$${Number(o.Cost).toFixed(2)}` : 'Free',
        });
      });
    });
    return rows;
  };

  setPriceDraft = (key, value) => {
    this.setState((prev) => ({
      priceDrafts: { ...prev.priceDrafts, [key]: value },
    }));
  };

  savePrices = async () => {
    const menuId = Number(this.props.match.params.menuId);
    const rows = this.priceRows();
    const existingProduct = {};
    const existingOption = {};
    (this.props.detail?.Prices || []).forEach((p) => {
      existingProduct[MenuDetail.priceKey(p.ProductId, p.VariationId)] = Number(p.Price);
    });
    (this.props.detail?.OptionPrices || []).forEach((p) => {
      existingOption[MenuDetail.optionKey(p.OptionId)] = Number(p.Price);
    });

    const toUpsertProduct = [];
    const toClearProduct = [];
    const toUpsertOption = [];
    const toClearOption = [];

    rows.forEach((row) => {
      const raw = (this.state.priceDrafts[row.key] ?? '').toString().trim();
      if (row.kind === 'option') {
        if (raw === '') {
          if (existingOption[row.key] !== undefined) toClearOption.push(row.optionId);
          return;
        }
        const num = Number(raw);
        if (Number.isNaN(num) || num < 0) return;
        if (existingOption[row.key] === undefined || Number(existingOption[row.key]) !== num) {
          toUpsertOption.push({ OptionId: row.optionId, ProductId: row.productId, Price: num });
        }
        return;
      }

      if (raw === '') {
        if (existingProduct[row.key] !== undefined) {
          toClearProduct.push({ ProductId: row.productId, VariationId: row.variationId });
        }
        return;
      }
      const num = Number(raw);
      if (Number.isNaN(num) || num < 0) return;
      if (existingProduct[row.key] === undefined || Number(existingProduct[row.key]) !== num) {
        toUpsertProduct.push({
          ProductId: row.productId,
          VariationId: row.variationId,
          Price: num,
        });
      }
    });

    this.setState({ savingPrices: true });
    let didSave = false;
    if (toUpsertProduct.length > 0) {
      didSave = true;
      await this.props.dispatch(upsertMenuPrices(menuId, toUpsertProduct));
    }
    for (const item of toClearProduct) {
      didSave = true;
      await this.props.dispatch(clearMenuPrice(menuId, item.ProductId, item.VariationId));
    }
    if (toUpsertOption.length > 0) {
      didSave = true;
      await this.props.dispatch(upsertMenuOptionPrices(menuId, toUpsertOption));
    }
    for (const optionId of toClearOption) {
      didSave = true;
      await this.props.dispatch(clearMenuOptionPrice(menuId, optionId));
    }
    if (!didSave) {
      await this.props.dispatch(loadMenuDetail(menuId));
    }
    this.setState({ savingPrices: false });
  };

  render() {
    const { detail, categories, isUpdating } = this.props;
    const menuId = Number(this.props.match.params.menuId);
    const menuProducts = this.menuProducts();
    const priceRows = this.priceRows();

    return (
      <div className={s.page}>
        <div className="mb-3">
          <Link to="/admin/products/menus"><i className="zmdi zmdi-arrow-left" aria-hidden="true"></i> Back to Menus</Link>
        </div>

        {!detail && (
          <Alert color="info" className="alert-sm">Loading menu…</Alert>
        )}

        {detail && (
          <>
            <Widget>
              <h5 className="mb-1">{detail.Name}</h5>
              <small className="text-muted">
                Order {detail.SortOrder} · {detail.IsActive ? 'Active' : 'Archived'} · Menu #{menuId}
              </small>
            </Widget>

            <div className={s.detailRow}>
              <div className={s.detailCol}>
                <Widget className={s.panel}>
                  <h6>Assign categories</h6>
                  <p className="text-muted small">
                    Products in selected categories appear on this menu. A category can belong to multiple menus.
                  </p>
                  <div className={s.panelBody}>
                    {(categories || []).length === 0 ? (
                      <Alert color="warning" className="alert-sm">No categories yet. Create categories first.</Alert>
                    ) : (
                      <div className={s.categoryList}>
                        {(categories || []).map((c) => (
                          <FormGroup check key={c.CategoryId} className="mb-0">
                            <CustomInput
                              type="checkbox"
                              id={`menu-cat-${c.CategoryId}`}
                              label={c.Name || c.CategoryName}
                              checked={this.state.selectedCategoryIds.includes(c.CategoryId)}
                              onChange={() => this.toggleCategory(c.CategoryId)}
                            />
                          </FormGroup>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className={s.panelActions}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={this.state.savingCategories || isUpdating}
                      onClick={this.saveCategories}
                    >
                      {this.state.savingCategories ? 'Saving…' : 'Save categories'}
                    </button>
                  </div>
                </Widget>
              </div>

              <div className={s.detailCol}>
                <Widget className={s.panel}>
                  <h6>Per-menu prices (optional)</h6>
                  <p className="text-muted small">
                    Blank = use base price / variation cost / option cost. Overrides apply to this menu only.
                    Free options can be given a paid override.
                  </p>
                  <div className={s.panelBody}>
                    {menuProducts.length === 0 ? (
                      <Alert color="info" className="alert-sm">Assign categories to set menu prices.</Alert>
                    ) : (
                      <div className={`table-responsive ${s.priceTableWrap}`}>
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>Item</th>
                              <th>Base</th>
                              <th>Menu override</th>
                            </tr>
                          </thead>
                          <tbody>
                            {priceRows.map((row) => (
                              <tr key={row.key}>
                                <td>{row.label}</td>
                                <td>{row.baseLabel}</td>
                                <td>
                                  <input
                                    className="form-control form-control-sm"
                                    style={{ maxWidth: 120 }}
                                    placeholder="base"
                                    value={this.state.priceDrafts[row.key] ?? ''}
                                    onChange={(e) => this.setPriceDraft(row.key, e.target.value)}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                  <div className={s.panelActions}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={this.state.savingPrices || isUpdating || menuProducts.length === 0}
                      onClick={this.savePrices}
                    >
                      {this.state.savingPrices ? 'Saving…' : 'Save prices'}
                    </button>
                  </div>
                </Widget>
              </div>
            </div>

            <Widget className="mt-2">
              <h6>Auto schedule windows</h6>
              <p className="text-muted small mb-2">
                Used when merchant default or a station is set to Auto schedule.
                Times use the merchant store timezone (not the browser clock).
                Same-day windows are active from start up to but not including end
                (09:00–17:00 ends at 5:00 PM). End before start is overnight into the next morning.
                Start equal to end is that whole weekday. Overlaps: latest start wins.
                Gaps use the merchant default menu, or All if none is set.
              </p>
              {this.state.scheduleError && (
                <Alert color="danger" className="alert-sm">{this.state.scheduleError}</Alert>
              )}
              {this.state.scheduleLoading ? (
                <Alert color="info" className="alert-sm">Loading schedules…</Alert>
              ) : (
                <div className={`table-responsive ${s.priceTableWrap}`}>
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Day</th>
                        <th>Start</th>
                        <th>End</th>
                        <th>Window</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {this.state.scheduleDrafts.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-muted">No windows yet. Add one for each day this menu should be active.</td>
                        </tr>
                      ) : (
                        this.state.scheduleDrafts.map((row, index) => (
                          <tr key={`sched-${index}`}>
                            <td>
                              <select
                                className="form-control form-control-sm"
                                value={row.DayOfWeek}
                                onChange={(e) => this.updateScheduleRow(index, 'DayOfWeek', Number(e.target.value))}
                              >
                                {DAY_LABELS.map((label, dow) => (
                                  <option key={label} value={dow}>{label}</option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <input
                                type="time"
                                className="form-control form-control-sm"
                                value={row.StartTime}
                                onChange={(e) => this.updateScheduleRow(index, 'StartTime', e.target.value)}
                              />
                            </td>
                            <td>
                              <input
                                type="time"
                                className="form-control form-control-sm"
                                value={row.EndTime}
                                onChange={(e) => this.updateScheduleRow(index, 'EndTime', e.target.value)}
                              />
                            </td>
                            <td className="text-muted small align-middle">
                              {windowKind(row.StartTime, row.EndTime)}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() => this.removeScheduleRow(index)}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={this.addScheduleRow}
                  disabled={this.state.savingSchedule || isUpdating}
                >
                  Add window
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={this.saveSchedules}
                  disabled={this.state.savingSchedule || this.state.scheduleLoading || isUpdating}
                >
                  {this.state.savingSchedule ? 'Saving…' : 'Save schedule'}
                </button>
              </div>
            </Widget>
          </>
        )}
      </div>
    );
  }
}

function mapStateToProps(store) {
  return {
    detail: store.menus.detail,
    categories: store.products.categories,
    products: store.products.data,
    isReceiving: store.menus.isReceiving,
    isUpdating: store.menus.isUpdating,
  };
}

export default withRouter(connect(mapStateToProps)(MenuDetail));
